import React, { useState } from "react";
import {
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  Button,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Select,
  Option,
  Alert,
  Divider,
  Box,
  ListItemDecorator,
  Avatar,
} from "@mui/joy";
import { JoyThemeProvider } from "../joy-theme-provider";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import {
  useAddBankAccountMutation,
  AddBankAccountCA,
  AddBankAccountUS,
} from "components/Slices/usersApiSlice";
import { CANADIAN_BANKS, getLogoUrl } from "lib/bankLogos";

interface AddBankAccountModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  userCountry?: string;
}

type Country = "CA" | "US";

export const AddBankAccountModal = ({
  open,
  onClose,
  onSuccess,
  userCountry,
}: AddBankAccountModalProps) => {
  const [addBankAccount, { isLoading }] = useAddBankAccountMutation();
  const [error, setError] = useState<string | null>(null);

  // Country is locked to the user's profile country
  const country: Country = userCountry === "US" ? "US" : "CA";

  // Common fields
  const [accountHolderName, setAccountHolderName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");

  // Canada-specific fields
  const [selectedBank, setSelectedBank] = useState<string | null>(null);
  const [institutionNumber, setInstitutionNumber] = useState("");
  const [isCustomInstitution, setIsCustomInstitution] = useState(false);
  const [transitNumber, setTransitNumber] = useState("");

  // US-specific fields
  const [routingNumber, setRoutingNumber] = useState("");

  const resetForm = () => {
    setAccountHolderName("");
    setAccountNumber("");
    setSelectedBank(null);
    setInstitutionNumber("");
    setIsCustomInstitution(false);
    setTransitNumber("");
    setRoutingNumber("");
    setError(null);
  };

  const handleBankChange = (value: string | null) => {
    if (value === "custom") {
      setIsCustomInstitution(true);
      setSelectedBank(null);
      setInstitutionNumber("");
    } else {
      setIsCustomInstitution(false);
      setSelectedBank(value);
      const bank = CANADIAN_BANKS.find((b) => b.institutionNumber === value);
      if (bank) {
        setInstitutionNumber(bank.institutionNumber);
      }
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      if (country === "CA") {
        const data: AddBankAccountCA = {
          country: "CA",
          accountHolderName,
          institutionNumber,
          transitNumber,
          accountNumber,
        };
        await addBankAccount(data).unwrap();
      } else {
        const data: AddBankAccountUS = {
          country: "US",
          accountHolderName,
          routingNumber,
          accountNumber,
        };
        await addBankAccount(data).unwrap();
      }

      resetForm();
      onSuccess?.();
      onClose();
    } catch (err: any) {
      console.error("Error adding bank account:", err);
      setError(
        err?.data?.error || err?.data?.message || "Failed to add bank account. Please try again.",
      );
    }
  };

  return (
    <JoyThemeProvider>
    <Modal open={open} onClose={handleClose}>
      <ModalDialog
        variant="outlined"
        sx={{ width: { xs: "90%", sm: 450 }, maxWidth: "100%" }}
      >
        <ModalClose />
        <Typography
          level="title-lg"
          startDecorator={<AccountBalanceIcon />}
          sx={{ mb: 1 }}
        >
          Add Payout Account
        </Typography>
        <Typography level="body-sm" sx={{ mb: 2 }}>
          Enter your bank account details to receive payouts.
        </Typography>

        <Divider sx={{ my: 1 }} />

        {error && (
          <Alert color="danger" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            {/* Country — locked to user's profile country */}
            <FormControl required>
              <FormLabel>Country</FormLabel>
              <Select value={country} disabled>
                <Option value="CA">Canada</Option>
                <Option value="US">United States</Option>
              </Select>
            </FormControl>

            {/* Account Holder Name */}
            <FormControl required>
              <FormLabel>Account Holder Name</FormLabel>
              <Input
                placeholder="John Doe"
                value={accountHolderName}
                onChange={(e) => setAccountHolderName(e.target.value)}
              />
            </FormControl>

            {/* Canada-specific fields */}
            {country === "CA" && (
              <>
                {/* Bank Selector */}
                <FormControl required>
                  <FormLabel>Select Your Bank (Institution Number)</FormLabel>
                  <Select
                    placeholder="Choose your bank..."
                    value={isCustomInstitution ? "custom" : selectedBank}
                    onChange={(_, value) => handleBankChange(value)}
                    slotProps={{
                      listbox: {
                        sx: { maxHeight: 300 },
                      },
                    }}
                  >
                    {CANADIAN_BANKS.map((bank) => (
                      <Option
                        key={bank.institutionNumber}
                        value={bank.institutionNumber}
                      >
                        <ListItemDecorator>
                          <Avatar
                            src={getLogoUrl(bank.domain)}
                            size="sm"
                            sx={{ width: 24, height: 24 }}
                          >
                            {getLogoUrl(bank.domain)}
                          </Avatar>
                        </ListItemDecorator>
                        <Box sx={{ ml: 1 }}>
                          <Typography level="body-sm" fontWeight={500}>
                            {bank.name}
                          </Typography>
                          <Typography
                            level="body-xs"
                            sx={{ color: "text.tertiary" }}
                          >
                            Institution #{bank.institutionNumber}
                          </Typography>
                        </Box>
                      </Option>
                    ))}
                    <Divider sx={{ my: 1 }} />
                    <Option value="custom">
                      <ListItemDecorator>
                        <Avatar size="sm" sx={{ width: 24, height: 24 }}>
                          <EditIcon sx={{ fontSize: 14 }} />
                        </Avatar>
                      </ListItemDecorator>
                      <Typography
                        level="body-sm"
                        fontWeight={500}
                        sx={{ ml: 1 }}
                      >
                        Other / Enter Manually
                      </Typography>
                    </Option>
                  </Select>
                </FormControl>

                {/* Custom Institution Number (only when "Other" is selected) */}
                {isCustomInstitution && (
                  <FormControl required>
                    <FormLabel>Institution Number</FormLabel>
                    <Input
                      placeholder="001"
                      value={institutionNumber}
                      onChange={(e) => setInstitutionNumber(e.target.value)}
                      slotProps={{
                        input: { maxLength: 3 },
                      }}
                    />
                    <Typography
                      level="body-xs"
                      sx={{ color: "text.tertiary", mt: 0.5 }}
                    >
                      3-digit institution number from your cheque
                    </Typography>
                  </FormControl>
                )}

                {/* Transit Number */}
                <FormControl required>
                  <FormLabel>Transit Number</FormLabel>
                  <Input
                    placeholder="12345"
                    value={transitNumber}
                    onChange={(e) => setTransitNumber(e.target.value)}
                    slotProps={{
                      input: { maxLength: 5 },
                    }}
                  />
                  <Typography
                    level="body-xs"
                    sx={{ color: "text.tertiary", mt: 0.5 }}
                  >
                    5-digit transit/branch number
                  </Typography>
                </FormControl>
              </>
            )}

            {/* US-specific fields */}
            {country === "US" && (
              <FormControl required>
                <FormLabel>Routing Number</FormLabel>
                <Input
                  placeholder="110000000"
                  value={routingNumber}
                  onChange={(e) => setRoutingNumber(e.target.value)}
                  slotProps={{
                    input: { maxLength: 9 },
                  }}
                />
              </FormControl>
            )}

            {/* Account Number */}
            <FormControl required>
              <FormLabel>Account Number</FormLabel>
              <Input
                placeholder={country === "CA" ? "1234567" : "000123456789"}
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
                slotProps={{
                  input: { maxLength: country === "CA" ? 12 : 17 },
                }}
              />
            </FormControl>

            <Divider sx={{ my: 1 }} />

            {/* Security Note */}
            <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
              Your bank details are encrypted and securely transmitted to
              Stripe.
            </Typography>

            {/* Submit Button */}
            <Button
              type="submit"
              loading={isLoading}
              startDecorator={<SaveIcon />}
              sx={{ mt: 1 }}
            >
              Add Bank Account
            </Button>
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
    </JoyThemeProvider>
  );
};
